<template>
  <ClientOnly>
    <div
      v-if="(topics && computedTopics) || (audiences && computedAudiences)"
      :class="tagSizeClass"
      class="tag-list"
    >
      <div v-if="firstTag && !showAll" class="button-group">
        <TagsItem
          :link="firstTag.link"
          :no-link="noLinks"
          :theme="firstTag.theme"
          :title="firstTag.title"
        />
      </div>
      <div v-else class="button-group flex-wrap list-all">
        <TagsItem
          v-for="tag in allTags"
          :key="tag.id"
          :link="tag.link"
          :theme="tag.theme"
          :title="tag.title"
        />
      </div>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    audiences?: string[]
    topics?: string[]
    size?: string
    showAll?: boolean
    sorting?: boolean
    useAudienceAsPrimary?: boolean
    noLinks?: boolean
  }>(),
  {
    audiences: () => [],
    topics: () => [],
    size: null,
    showAll: false,
    sorting: false,
    useAudienceAsPrimary: false,
    noLinks: false,
  },
)
const { fetchAudiences, fetchTopics } = useStoryblokDataSources()
const { data: topicTags } = await fetchTopics()
const { data: audienceTags } = await fetchAudiences()
const route = useRoute()

const computedTopics = computed(() => {
  if (!topicTags.value) {
    return []
  }

  // filter to relevant tags
  return topicTags.value
    .filter((topic) => props.topics.includes(topic.value))
    .sort((topicA, topicB) => {
      // if the tag is in the url topics filter query, push to top
      if (
        ((route?.query?.topics as string) || '')
          .split(',')
          .includes(topicA.value)
      )
        return -1
      // CMS-based topics order
      if (props.sorting)
        return (
          props.topics.indexOf(topicA.value) -
          props.topics.indexOf(topicB.value)
        )
      // default sorting
      return topicA.name.localeCompare(topicB.name)
    })
    .map((topic) => ({
      id: topic.id,
      title: topic.name,
      link: '/featured-topics/' + topic.value,
      theme: getTheme(topic.color || 0),
    }))
})

const computedAudiences = computed(() => {
  let availableOptions = [...props.audiences]

  availableOptions = availableOptions.filter((item) => item !== 'all')

  if (!availableOptions.length) {
    return []
  }

  // filter to relevant tags
  return audienceTags.value
    .filter((audience) => availableOptions.includes(audience.value))
    .sort((audienceA, audienceB) => {
      // if the tag is in the url topics filter query, push to top
      if (
        ((route?.query?.topics as string) || '')
          .split(',')
          .includes(audienceA.value)
      )
        return -1
      // CMS-based audiences order
      if (props.sorting)
        return (
          props.audiences.indexOf(audienceA.value) -
          props.audiences.indexOf(audienceB.value)
        )
      // default sorting
      return audienceA.name.localeCompare(audienceB.name)
    })
    .map((audience) => ({
      id: audience.id,
      title: audience.name,
      link: '/audiences/' + audience.value,
      theme: getTheme(audience.color || 0),
    }))
})

const firstTag = computed(() =>
  props.useAudienceAsPrimary && computedAudiences.value?.[0]
    ? computedAudiences.value[0]
    : computedTopics.value?.[0],
)

const allTags = computed(() =>
  computedTopics.value?.concat(computedAudiences.value),
)

const tagSizeClass = computed(() =>
  props.size ? 'tag-list--' + props.size : false,
)

const getTheme = (val: string) => 'theme-' + val
</script>

<style lang="less" scoped>
.tag-list {
  > .button-group {
    margin: 0 0 @gutter-xxs;
    max-width: 100%;

    > .button {
      max-width: calc(100% - @45px);

      span {
        .overflow-text();
      }
    }

    &.list-all {
      max-width: @375px;
    }
  }
}

@media print {
  .tag-list {
    > .button-group {
      &.list-all {
        max-width: none;
      }
    }
  }
}
</style>
